import type { TDateISO } from "~/@types/TDateISO";

class InvalidSiteTimezoneError extends Error {
	readonly siteTimezone: string;
	readonly originalCause?: unknown;

	constructor(siteTimezone: string, cause?: unknown) {
		super(`Invalid IANA site timezone: '${siteTimezone}'`);
		this.name = "InvalidSiteTimezoneError";
		this.siteTimezone = siteTimezone;
		this.originalCause = cause;
	}
}

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

interface WallClockParts {
	year: number;
	month: number;
	day: number;
	hour: number;
	minute: number;
	second: number;
}

const readWallClock = (instantMs: number, siteTimezone: string): WallClockParts => {
	const formatter = new Intl.DateTimeFormat("en-US", {
		day: "2-digit",
		hour: "2-digit",
		hour12: false,
		minute: "2-digit",
		month: "2-digit",
		second: "2-digit",
		timeZone: siteTimezone,
		year: "numeric"
	});
	const parts = formatter.formatToParts(new Date(instantMs));
	const map: Record<string, string> = {};
	for (const p of parts) {
		if (p.type !== "literal") {
			map[p.type] = p.value;
		}
	}
	const hour = Number.parseInt(map.hour ?? "0", 10);
	return {
		day: Number.parseInt(map.day ?? "0", 10),
		hour: hour === 24 ? 0 : hour,
		minute: Number.parseInt(map.minute ?? "0", 10),
		month: Number.parseInt(map.month ?? "0", 10),
		second: Number.parseInt(map.second ?? "0", 10),
		year: Number.parseInt(map.year ?? "0", 10)
	};
};

const diffFromTargetMs = (actual: WallClockParts, target: WallClockParts): number => {
	const actualUtc = Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute, actual.second);
	const targetUtc = Date.UTC(target.year, target.month - 1, target.day, target.hour, target.minute, target.second);
	return actualUtc - targetUtc;
};

const toInstant = (dateOnly: string, siteTimezone: string): TDateISO => {
	const tz = (siteTimezone ?? "").trim();
	if (tz.length === 0) {
		throw new InvalidSiteTimezoneError(siteTimezone);
	}
	const match = DATE_ONLY_PATTERN.exec(dateOnly ?? "");
	if (!match) {
		throw new TypeError(`siteTimezone.toInstant expects YYYY-MM-DD, received '${dateOnly}'`);
	}
	const year = Number.parseInt(match[1], 10);
	const month = Number.parseInt(match[2], 10);
	const day = Number.parseInt(match[3], 10);

	let formatter: Intl.DateTimeFormat;
	try {
		formatter = new Intl.DateTimeFormat("en-US", { timeZone: tz });
	} catch (cause) {
		throw new InvalidSiteTimezoneError(tz, cause);
	}
	formatter.format(new Date(0));

	const target: WallClockParts = {
		day,
		hour: 0,
		minute: 0,
		month,
		second: 0,
		year
	};

	let candidate = Date.UTC(year, month - 1, day, 0, 0, 0);
	for (let i = 0; i < 3; i += 1) {
		const wall = readWallClock(candidate, tz);
		const drift = diffFromTargetMs(wall, target);
		if (drift === 0) {
			return new Date(candidate).toISOString() as TDateISO;
		}
		candidate -= drift;
	}
	return new Date(candidate).toISOString() as TDateISO;
};

const dateOnly = (iso: string, siteTimezone: string): string => {
	const tz = (siteTimezone ?? "").trim();
	if (tz.length === 0) return iso;
	const ms = Date.parse(iso);
	if (Number.isNaN(ms)) return iso;
	try {
		const formatter = new Intl.DateTimeFormat("en-US", {
			day: "2-digit",
			month: "2-digit",
			timeZone: tz,
			year: "numeric"
		});
		const parts = formatter.formatToParts(new Date(ms));
		const map: Record<string, string> = {};
		for (const p of parts) {
			if (p.type !== "literal") {
				map[p.type] = p.value;
			}
		}
		const year = (map.year ?? "").padStart(4, "0");
		const month = (map.month ?? "").padStart(2, "0");
		const day = (map.day ?? "").padStart(2, "0");
		return `${year}-${month}-${day}`;
	} catch {
		return iso;
	}
};

const siteTimezone = {
	InvalidSiteTimezoneError,
	dateOnly,
	toInstant
};

export default siteTimezone;
